import { Box, Typography } from "@mui/material";
import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import {
  DEFAULT_DRAG_HANDLE_SELECTOR,
  NO_DRAG_SELECTOR,
} from "../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import DialogHeader from "../../../common/components/functional/dialogs/DialogHeader";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../common/helpers/isEmpty";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { getCoreUserFullName } from "../../../common/utils/users";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import { canExpertEditCustomer } from "../../user/accessRightChecks";
import InvoiceReferencesSection from "../companyInfo/InvoiceReferencesSection";
import { FormContainer } from "../customerManagement/styles";
import { selectCustomerOrganizationById } from "../customerOrganizationsSlice";
import {
  selectCustomerUsersByIds,
  selectFormattedCustomerUserByIdOrNull,
} from "../customerUsersSlice";
import {
  addCustomerWorkLocation,
  getCityAndPostOfficeByZip,
  selectCustomerContractById,
  selectCustomerWorkLocationById,
  updateCustomerWorkLocation,
  updateWorkLocationActiveStatus,
} from "../customerWorkLocationsSlice";
import { ActionButtonsContainer, ModalContainer } from "../styles";
import { WorkLocation } from "../types/queries";
import { isEmptyPhoneNumber } from "../utils";
import BasicInfo from "./BasicInfo";
import ContactInfo from "./ContactInfo";
import CostCenters from "./CostCenters";
import DescriptionsTable from "./DescriptionsTable";
import { WorkLocationFormValues } from "./types";

type Props = {
  workLocationId?: string;
  onClose: () => void;
};

export const NAME_SPACE = "customers:workLocationNS";

const WorkLocationsForm = ({ workLocationId, onClose }: Props) => {
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { customerCompanyId } = useParams();
  const customerOrgId = searchParams.get("org");

  const { status } = useAppSelector((state) => state.customerWorkLocations);
  const isLoading = status === "pending";

  const selectedWorkLocation: WorkLocation = useAppSelector((state) =>
    selectCustomerWorkLocationById(state, workLocationId as string),
  );

  const selectedOrganization = useAppSelector((state) =>
    selectCustomerOrganizationById(state, customerOrgId as string),
  );

  const selectedContract = useAppSelector((state) =>
    selectCustomerContractById(state, selectedWorkLocation?.contractId),
  );

  const customerUsers = useAppSelector(
    selectCustomerUsersByIds(
      selectedWorkLocation?.talentCustomerContactPersons,
    ),
  );

  const talentCustomerContactPersonsDefaultValues = customerUsers.map(
    (customerUser) => ({
      id: customerUser.id,
      name: getCoreUserFullName(customerUser),
      tooltip: `${t("talentProfile:phoneNumberLong")}: ${!isEmptyPhoneNumber(customerUser.account?.phone) ? customerUser.account?.phone : t("common:notSet")}`,
    }),
  );

  const invoiceReferences = selectedWorkLocation?.invoiceReferences
    ? {
        orderIdentifier:
          selectedWorkLocation?.invoiceReferences?.orderIdentifier || "",
        agreementIdentifier:
          selectedWorkLocation?.invoiceReferences?.agreementIdentifier || "",
        sellerReferenceIdentifier:
          selectedWorkLocation?.invoiceReferences?.sellerReferenceIdentifier ||
          "",
        buyerReferenceIdentifier:
          selectedWorkLocation?.invoiceReferences?.buyerReferenceIdentifier ||
          "",
      }
    : null;

  const invoiceContactPerson = useAppSelector(
    selectFormattedCustomerUserByIdOrNull(
      selectedWorkLocation?.invoiceContactPerson?.id,
    ),
  );

  const defaultValues: WorkLocationFormValues = {
    name: selectedWorkLocation?.name || "",
    contract: !!selectedContract
      ? {
          id: selectedContract.id,
          name: selectedContract.name,
        }
      : null,
    salesPerson: selectedWorkLocation?.salesPersonId
      ? {
          id: selectedWorkLocation.salesPersonId,
          name: selectedWorkLocation.salesPersonName,
        }
      : null,
    descriptionForEezy: selectedWorkLocation?.descriptionForEezy || "",
    street: selectedWorkLocation?.address.street || "",
    zip: selectedWorkLocation?.address.zip || "",
    city: "",
    postOffice: "",
    descriptionForTalent: selectedWorkLocation?.descriptionForTalent || "",
    descriptionForTalentEng:
      selectedWorkLocation?.descriptionForTalentEng || "",
    customerCCs: selectedWorkLocation?.customerCCs || [],
    info: selectedWorkLocation?.info || {},
    talentCustomerContactPersons: talentCustomerContactPersonsDefaultValues,
    additionalInfo: selectedWorkLocation?.additionalInfo || "",
    allowUsingAllCostCenters: false,
    invoiceContactPerson,
    invoiceReferences,
  };

  const methods = useForm({ defaultValues, mode: "onChange" });
  const watchedZip = methods.watch("zip");

  const dispatchGetCityAndPostOfficeByZip = useCallback(
    (zip: string, firstRender: boolean) => {
      dispatch(getCityAndPostOfficeByZip({ zip })).then((action) => {
        if (isFulfilled(action)) {
          methods.clearErrors(["zip", "city", "postOffice"]);
          if (firstRender) {
            methods.reset({ ...defaultValues, ...action.payload });
          } else {
            Object.entries(action.payload).forEach(([key, value]) => {
              methods.setValue(key as "city" | "postOffice", value, {
                shouldDirty: true,
              });
            });
          }
        } else if (isRejected(action)) {
          const addressValues = {
            city: "",
            postOffice: "",
            zip: action.meta.arg.zip,
          };

          const rest = methods.getValues();

          methods.reset({ ...rest, ...addressValues });

          Object.keys(addressValues).forEach((key) => {
            methods.setError(key as keyof WorkLocationFormValues, {
              type: "invalidZip",
              message: "",
            });
          });
        }
      });
    },
    // eslint-disable-next-line
    [dispatch, methods],
  );

  useEffect(() => {
    if (defaultValues.zip && !methods.formState.isDirty) {
      // reset form on first render with city and post office values
      dispatchGetCityAndPostOfficeByZip(defaultValues.zip, true);
    }
  }, [defaultValues.zip, dispatch, methods, dispatchGetCityAndPostOfficeByZip]);

  useEffect(() => {
    // set city and post office values on zip change, set dirty to true
    if (watchedZip?.length === 5 && methods.formState.isDirty) {
      dispatchGetCityAndPostOfficeByZip(watchedZip, false);
    }
  }, [
    watchedZip,
    dispatchGetCityAndPostOfficeByZip,
    methods.formState.isDirty,
  ]);

  const onSave = async (data: WorkLocationFormValues) => {
    const {
      street,
      zip,
      city,
      customerCCs,
      descriptionForEezy,
      contract,
      salesPerson,
      additionalInfo,
      invoiceContactPerson,
      invoiceReferences,
      ...rest
    } = data;

    const address = {
      street,
      zip,
      city,
      country: "FI", // TODO: ???
    };

    const isValidContactPerson = (item: unknown): item is AutocompleteType =>
      typeof item === "object" &&
      item !== null &&
      !!(item as AutocompleteType).id;

    const talentCustomerContactPersons = data.talentCustomerContactPersons
      .filter(isValidContactPerson)
      .map(({ id }) => ({ id }));

    const workLocation: WorkLocation = {
      ...rest,
      ...(salesPerson?.id && {
        salesPersonId: salesPerson.id,
        salesPersonName: salesPerson.name,
      }),
      descriptionForEezy,
      id: selectedWorkLocation?.id as string,
      version: selectedWorkLocation?.version as number,
      customerOrgId: customerOrgId as string,
      contractId: contract?.id || "",
      additionalInfo: selectedWorkLocation?.additionalInfo,
      address,
      customerCCs: methods.getValues("allowUsingAllCostCenters")
        ? []
        : customerCCs,
      talentCustomerContactPersons,
      invoiceReferences,
      invoiceContactPerson: invoiceContactPerson
        ? {
            id: invoiceContactPerson.id,
          }
        : null,
      active: selectedWorkLocation ? selectedWorkLocation?.active : true,
    };

    if (selectedWorkLocation) {
      return dispatch(updateCustomerWorkLocation(workLocation)).then(() =>
        onClose(),
      );
    }

    dispatch(addCustomerWorkLocation(workLocation)).then((action) => {
      if (isFulfilled(action)) return onClose();
    });
  };

  const handleStatusChange = () => {
    const params = {
      id: workLocationId as string,
      version: selectedWorkLocation?.version as number,
      active: !selectedWorkLocation?.active,
    };

    dispatch(updateWorkLocationActiveStatus(params)).then((action) => {
      if (isFulfilled(action)) return onClose();
    });
  };

  const workLocationName = methods.watch("name");
  const companyName = selectedOrganization?.name;

  const activeStatusLabel = selectedWorkLocation?.active
    ? t("customers:disable")
    : t("common:active");

  const isValid = methods.formState.isValid;

  const saveDisabled =
    !methods.formState.isDirty ||
    isEmpty(methods.formState.dirtyFields) ||
    methods.formState.isSubmitting ||
    !isEmpty(methods.formState.errors) ||
    !isValid;

  const TitleComponent = (
    <Box display="flex" alignItems="center" gap={workLocationName ? "1rem" : 0}>
      <Typography variant="header" fontSize="22px" fontWeight="600">
        {workLocationName}
      </Typography>
      <Typography>{companyName}</Typography>
    </Box>
  );

  return (
    <ModalContainer sx={{ cursor: "auto" }}>
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <DialogHeader title={TitleComponent} onClose={onClose} />
          <FormContainer maxWidth="975px" className={NO_DRAG_SELECTOR}>
            <OverlaySpinner
              sx={{ borderRadius: "12px" }}
              isLoading={isLoading}
            />
            <BasicInfo />
            {customerCompanyId && (
              <ContactInfo customerCompanyId={customerCompanyId} />
            )}
            <CostCenters />
            <DescriptionsTable />
            <InvoiceReferencesSection
              tooltipTitle={t(
                "customers:workLocationNS.invoiceReferencesTooltip",
              )}
            />
          </FormContainer>
        </UnsavedChangesProvider>
      </FormProvider>
      <ActionButtonsContainer
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        marginTop="1rem"
      >
        <CustomButton
          accessRightChecks={[canExpertEditCustomer]}
          disabled={saveDisabled}
          variant="contained"
          sx={{ borderRadius: "18px", height: "36px" }}
          onClick={methods.handleSubmit(onSave)}
        >
          {t("common:save")}
        </CustomButton>
        <CustomButton
          accessRightChecks={[canExpertEditCustomer]}
          variant="outlined"
          color={!selectedWorkLocation?.active ? "primary" : "error"}
          sx={{
            borderRadius: "18px",
            height: "36px",
            visibility: selectedWorkLocation ? "visible" : "hidden",
          }}
          onClick={handleStatusChange}
        >
          {activeStatusLabel}
        </CustomButton>
      </ActionButtonsContainer>
    </ModalContainer>
  );
};

export default WorkLocationsForm;
